import {forwardRef, useCallback} from 'react'
import {NavLink, NavLinkProps, useLocation} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'

export interface RefreshingNavLinkProps extends NavLinkProps {
    /**
     * Query keys to invalidate on click. If not provided, all queries will be invalidated.
     */
    invalidateKeys?: string[]
}

/**
 * A NavLink that invalidates React Query cache when clicked.
 * This ensures fresh data is fetched when navigating to a page.
 */
export const RefreshingNavLink = forwardRef<HTMLAnchorElement, RefreshingNavLinkProps>(
    ({invalidateKeys, onClick, to, ...props}, ref) => {
        const queryClient = useQueryClient()
        const location = useLocation()

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLAnchorElement>) => {
                // Only invalidate if navigating to a different path
                const targetPath = typeof to === 'string' ? to : to.pathname
                if (targetPath && location.pathname !== targetPath) {
                    if (invalidateKeys && invalidateKeys.length > 0) {
                        // Invalidate specific query keys
                        invalidateKeys.forEach(key => {
                            queryClient.invalidateQueries({queryKey: [key]})
                        })
                    } else {
                        // Invalidate all queries
                        queryClient.invalidateQueries()
                    }
                }

                // Call original onClick if provided
                onClick?.(e)
            },
            [queryClient, invalidateKeys, onClick, to, location.pathname]
        )

        return (
            // eslint-disable-next-line testid/require-testid -- data-testid is passed through via {...props} from callers
            <NavLink
                ref={ref}
                to={to}
                onClick={handleClick}
                {...props}
            />
        )
    }
)

RefreshingNavLink.displayName = 'RefreshingNavLink'
